<script lang="ts">
  import { t } from '$core/i18n';
  import {
    listMacroSessionsPage,
    listAttemptsBySession,
    softDeleteMacroSession,
    getNodePath,
    listNodes
  } from '$db/local/repositories';
  import type { MacroSession, ProblemAttempt, Node } from '$domain/types';
  import { Icon } from '$ui/primitives/Icon';
  import { EmptyState } from '$ui/primitives/EmptyState';
  import { Skeleton } from '$ui/primitives/Skeleton';

  const PAGE_SIZE = 20;

  let sessions = $state<MacroSession[]>([]);
  let attemptsMap = $state<Record<string, ProblemAttempt[]>>({});
  let pathsMap = $state<Record<string, Node[]>>({});
  let nodeTitlesMap = $state<Record<string, string>>({});
  let expandedId = $state<string | null>(null);
  let loading = $state(true);
  let errorMsg = $state<string | null>(null);

  $effect(() => {
    void loadHistory();
  });

  async function loadHistory() {
    loading = true;
    errorMsg = null;
    try {
      const page = await listMacroSessionsPage({ limit: PAGE_SIZE });
      sessions = page.sessions;

      // Cargar todos los nodos para tener un mapa de títulos rápido
      const allNodes = await listNodes();
      const titles: Record<string, string> = {};
      for (const n of allNodes) {
        titles[n.id] = n.title;
      }
      nodeTitlesMap = titles;

      // Cargar los paths de los nodos
      const paths: Record<string, Node[]> = {};
      for (const s of sessions) {
        if (s.nodeId) {
          paths[s.nodeId] = await getNodePath(s.nodeId);
        }
      }
      pathsMap = paths;

      // Cargar intentos de la sesión activa si hay alguna
      const open = sessions.find((s) => !s.endedAt);
      if (open) {
        expandedId = open.id;
        await loadAttempts(open.id);
      }
    } catch (err) {
      console.error(err);
      errorMsg = 'Error loading history.';
    } finally {
      loading = false;
    }
  }

  async function loadAttempts(sessionId: string) {
    if (attemptsMap[sessionId]) return;
    const attempts = await listAttemptsBySession(sessionId);
    attemptsMap = { ...attemptsMap, [sessionId]: attempts };
  }

  async function toggleExpand(sessionId: string) {
    if (expandedId === sessionId) {
      expandedId = null;
    } else {
      expandedId = sessionId;
      await loadAttempts(sessionId);
    }
  }

  async function deleteSession(sessionId: string) {
    if (!confirm('Are you sure you want to delete this session? This cannot be undone.')) return;
    try {
      await softDeleteMacroSession(sessionId);
      sessions = sessions.filter((s) => s.id !== sessionId);
    } catch (err) {
      console.error(err);
      alert('Failed to delete session.');
    }
  }

  function formatDuration(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }

  function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function formatTime(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  function getNodeChain(nodeId: string | undefined): string {
    if (!nodeId) return 'Unknown Topic';
    const path = pathsMap[nodeId];
    if (!path) return 'Loading...';
    return path.map(n => n.title).join(' > ');
  }
</script>

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('history.title')}</h1>
    <p class="view-subtitle">Your past study sessions and problem attempts.</p>
  </header>

  {#if loading}
    <div class="skeleton-list">
      <Skeleton height="80px" />
      <Skeleton height="80px" />
      <Skeleton height="80px" />
    </div>
  {:else if errorMsg}
    <p class="error">{errorMsg}</p>
  {:else if sessions.length === 0}
    <EmptyState
      icon="history"
      title="No history yet"
      description="Complete a study session to see it here."
    />
  {:else}
    <div class="history-list">
      {#each sessions as session, index (session.id)}
        {@const isExpanded = expandedId === session.id}
        {@const attempts = attemptsMap[session.id] || []}
        {@const exerciseCount = attempts.length}
        
        <div class="session-card" class:expanded={isExpanded} class:discarded={session.status === 'discarded'}>
          
          <div class="session-header" role="button" tabindex="0" onclick={() => toggleExpand(session.id)} onkeydown={(e) => e.key === 'Enter' && toggleExpand(session.id)}>
            
            <!-- Cabecera Principal -->
            <div class="session-main">
              <div class="session-badge">#{sessions.length - index}</div>
              
              <div class="session-info-grid">
                <div class="info-item">
                  <span class="info-label">Date</span>
                  <span class="info-value">{formatDate(session.startedAt)}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Time</span>
                  <span class="info-value">{formatTime(session.startedAt)}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Focus Duration</span>
                  <span class="info-value">{formatDuration(session.focusDurationMs)}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Exercises</span>
                  <span class="info-value">{exerciseCount} solved</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Mode</span>
                  <span class="info-value">{session.mode === 'A' ? 'Practice' : 'FSRS'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Status</span>
                  <span class="info-value" class:status-discarded={session.status === 'discarded'}>
                    {session.status === 'discarded' ? 'Discarded' : 'Saved'}
                  </span>
                </div>
              </div>
            </div>

            <div class="session-actions">
              <button class="icon-btn" title="Delete Session" onclick={(e) => { e.stopPropagation(); deleteSession(session.id); }}>
                <Icon name="trash" size={16} />
              </button>
              <button class="expand-btn" aria-label="Toggle details">
                <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size={18} />
              </button>
            </div>
          </div>

          <!-- Subcabecera: Cadena de Nodos + Stats Extras -->
          <div class="session-subheader">
            <div class="node-chain">
              <Icon name="layers" size={14} />
              <span>{getNodeChain(session.nodeId)}</span>
            </div>
            <div class="extra-stats">
              {#if session.readingDurationMs > 0}
                <span title="Reading Time"><Icon name="book-open" size={12}/> {formatDuration(session.readingDurationMs)}</span>
              {/if}
              {#if session.distractions > 0}
                <span title="Distractions"><Icon name="bell" size={12}/> {session.distractions}</span>
              {/if}
              {#if session.frustration != null}
                <span title="Frustration"><Icon name="alert-circle" size={12}/> {session.frustration}/10</span>
              {/if}
              {#if session.energy != null}
                <span title="Energy"><Icon name="zap" size={12}/> {session.energy}/10</span>
              {/if}
            </div>
          </div>

          {#if isExpanded}
            <div class="session-details">
              {#if session.nodeTimes && Object.keys(session.nodeTimes).length > 1}
                <div class="node-time-breakdown">
                  <h5 class="breakdown-title">Time per subject</h5>
                  <ul class="breakdown-list">
                    {#each Object.entries(session.nodeTimes) as [nodeId, timeMs]}
                      <li class="breakdown-item">
                        <span class="breakdown-name">{nodeTitlesMap[nodeId] || 'Unknown'}</span>
                        <span class="breakdown-time">{formatDuration(timeMs)}</span>
                      </li>
                    {/each}
                  </ul>
                </div>
              {/if}

              {#if attempts.length === 0}
                <p class="no-attempts">No problems registered in this session.</p>
              {:else}
                <table class="attempts-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Duration</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each attempts as attempt (attempt.id)}
                      <tr>
                        <td class="mono">Problem {attempt.problemNumber}</td>
                        <td class="mono">{formatDuration(attempt.durationMs)}</td>
                        <td>
                          <span class="result-badge result-{attempt.result}">
                            {attempt.result === 'correct' ? 'Good' : attempt.result === 'incorrect' ? 'Bad' : 'Unresolved'}
                          </span>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    width: 100%;
  }

  .view-head { display: flex; flex-direction: column; gap: var(--space-1); }
  .view-title { margin: 0; font-size: var(--text-h1); font-weight: var(--text-h1-weight); color: var(--color-text-primary); }
  .view-subtitle { margin: 0; font-size: var(--text-sm); color: var(--color-text-muted); }

  .skeleton-list { display: flex; flex-direction: column; gap: var(--space-2); }

  .history-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .session-card {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    overflow: hidden;
    transition: border-color 0.2s;
  }

  .session-card:hover { border-color: var(--color-border-strong); }
  .session-card.discarded { opacity: 0.7; border-left: 3px solid var(--color-danger); }

  .session-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-4);
    cursor: pointer;
    background: var(--color-surface);
    user-select: none;
  }

  .session-main {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    flex: 1;
  }

  .session-badge {
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-border);
    color: var(--color-text-muted);
    font-family: var(--font-mono);
    font-weight: 700;
    font-size: var(--text-sm);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    flex-shrink: 0;
  }

  .session-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: var(--space-3);
    flex: 1;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .info-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
    font-weight: 600;
  }

  .info-value {
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    font-weight: 500;
  }

  .status-discarded {
    color: var(--color-danger);
  }

  .session-subheader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 var(--space-4) var(--space-3);
    background: var(--color-surface);
    border-bottom: 1px solid var(--color-border);
  }

  .node-chain {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-xs);
    color: var(--color-text-muted);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 70%;
  }

  .extra-stats {
    display: flex;
    gap: var(--space-3);
    font-size: var(--text-xs);
    color: var(--color-text-muted);
  }

  .extra-stats span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .session-actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .icon-btn, .expand-btn {
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-1);
    border-radius: var(--radius-sm);
    transition: all 0.2s;
  }

  .icon-btn:hover { color: var(--color-danger); background: var(--color-danger-tint); }
  .expand-btn:hover { color: var(--color-text-primary); background: var(--color-surface-hover); }

  .session-details {
    padding: var(--space-4);
    background: var(--color-surface-sunken);
  }

  .node-time-breakdown {
    margin-bottom: var(--space-4);
    padding-bottom: var(--space-3);
    border-bottom: 1px dashed var(--color-border);
  }
  .breakdown-title {
    margin: 0 0 var(--space-2) 0;
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }
  .breakdown-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .breakdown-item {
    display: flex;
    justify-content: space-between;
    font-size: var(--text-sm);
    color: var(--color-text-primary);
  }
  .breakdown-name {
    font-weight: 500;
  }
  .breakdown-time {
    font-family: var(--font-mono);
    color: var(--color-text-secondary);
  }

  .no-attempts {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    margin: 0;
    text-align: center;
    font-style: italic;
  }

  .attempts-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm);
  }

  .attempts-table th {
    text-align: left;
    padding: var(--space-2);
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-muted);
    font-weight: 600;
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .attempts-table td {
    padding: var(--space-2);
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-primary);
  }

  .attempts-table tr:last-child td { border-bottom: none; }

  .mono { font-family: var(--font-mono); font-variant-numeric: tabular-nums; }

  .result-badge {
    display: inline-flex;
    padding: 2px 8px;
    border-radius: var(--radius-full);
    font-size: var(--text-xs);
    font-weight: 600;
  }

  .result-correct { background: var(--color-success-tint); color: var(--color-success); }
  .result-incorrect { background: var(--color-danger-tint); color: var(--color-danger); }
  .result-unresolved { background: var(--color-warning-tint); color: var(--color-warning); }

  .error {
    color: var(--color-danger);
    background: var(--color-danger-tint);
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    font-size: var(--text-sm);
  }
</style>